<script setup>
import { ref, computed, onMounted, watch } from 'vue';
import { getAuth, tenantDatabase } from "@/tenant";
import { getDatabase, ref as dbRef, update, get, set } from "firebase/database";
import store from "@/store";
import moment from 'moment';
import Editor from '@tinymce/tinymce-vue';

import IconTick from '~icons/my-icons/tick';
import PlusClose from '~icons/my-icons/cross';
import UserPlusIcon from '~icons/my-icons/user-plus';

const props = defineProps({
    clientId: String,
    notes: {
        type: [Array, String],
        default: () => []
    },
    isLoading: {
        type: Boolean,
        default: false
    }
});

const emit = defineEmits(['notesUpdated']);

const notes = ref([]);
const newNoteText = ref('');
const showAddForm = ref(false);
const editingIndex = ref(null);
const editingText = ref('');
const isLoading = ref(false);
const isInitialLoading = ref(true);

// Load and convert notes format
const loadNotes = () => {
    if (typeof props.notes === 'string' && props.notes.trim()) {
        // Convert old string format to array format - NO author info, just the text
        notes.value = [{
            text: props.notes,
            author: 'Unknown User', // Default fallback
            authorId: 'unknown',
            createdAt: Date.now(),
            updatedAt: Date.now()
        }];
    } else if (Array.isArray(props.notes)) {
        // Keep existing notes as-is, don't modify anything
        notes.value = [...props.notes];
    } else {
        notes.value = [];
    }

    isInitialLoading.value = false;
};

onMounted(async () => {
    await loadCurrentUser();
    loadNotes();
});

// Watch for prop changes
watch(() => props.notes, (newNotes) => {
    console.log('Props changed, new notes:', newNotes);
    loadNotes();
}, { deep: true });

const sortedNotes = computed(() => {
    return notes.value
        .map((note, index) => ({ ...note, originalIndex: index }))
        .sort((a, b) => b.createdAt - a.createdAt);
});

const currentUser = ref(null);

// Get current user from Firebase auth
const loadCurrentUser = async () => {
    try {
        const auth = await getAuth();
        if (auth.currentUser) {
            currentUser.value = {
                uid: auth.currentUser.uid,
                displayName: auth.currentUser.displayName,
                email: auth.currentUser.email
            };
        } else {
            // If no direct auth user, try from store
            const storeUser = store.getters.getUserState;
            if (storeUser) {
                currentUser.value = storeUser;
            }
        }
    } catch (error) {
        console.error('Error getting current user:', error);
        // Fallback to store
        const storeUser = store.getters.getUserState;
        if (storeUser) {
            currentUser.value = storeUser;
        }
    }
};

const formatDate = (timestamp) => {
    return moment(timestamp).format('MMM DD, YYYY at HH:mm');
};

const getUserName = (user) => {
    if (!user) return 'Unknown User';

    // Try displayName from Firebase auth
    if (user.displayName) {
        return user.displayName;
    }

    // Try userSettings from store (first/last name)
    if (user.userSettings?.firstName || user.userSettings?.lastName) {
        return `${user.userSettings?.firstName || ''} ${user.userSettings?.lastName || ''}`.trim();
    }

    // Fallback to email
    if (user.email) {
        return user.email.split('@')[0];
    }

    return 'Unknown User';
};

const addNote = async () => {
    if (!newNoteText.value.trim()) return;

    isLoading.value = true;
    try {
        const user = currentUser.value;

        const newNote = {
            text: newNoteText.value.trim(),
            author: getUserName(user),
            authorId: user?.uid || 'unknown',
            createdAt: Date.now(),
            updatedAt: Date.now()
        };

        notes.value.unshift(newNote); // Add to beginning of array
        newNoteText.value = '';
        showAddForm.value = false;

        await saveNotesToFirebase();
    } catch (error) {
        console.error('Error adding note:', error);
        // Revert the optimistic update
        loadNotes();
    } finally {
        isLoading.value = false;
    }
};

const startEdit = (originalIndex, text) => {
    editingIndex.value = originalIndex;
    editingText.value = text;
};

const cancelEdit = () => {
    editingIndex.value = null;
    editingText.value = '';
};

const saveEdit = async () => {
    if (!editingText.value.trim()) return;

    isLoading.value = true;
    try {
        const originalIndex = editingIndex.value;
        const user = currentUser.value;

        // Create a new array with the updated note to maintain proper array structure
        const updatedNotes = [...notes.value];
        updatedNotes[originalIndex] = {
            ...updatedNotes[originalIndex],
            text: editingText.value.trim(),
            updatedAt: Date.now(),
            lastEditedBy: getUserName(user),
            lastEditedById: user?.uid || 'unknown'
        };
        notes.value = updatedNotes;

        editingIndex.value = null;
        editingText.value = '';

        await saveNotesToFirebase();
    } catch (error) {
        console.error('Error saving edit:', error);
        // Revert the optimistic update
        loadNotes();
    } finally {
        isLoading.value = false;
    }
};

const deleteNote = async (originalIndex) => {
    if (!confirm('Are you sure you want to delete this note?')) return;

    isLoading.value = true;
    try {
        // Create a new array without the deleted note to maintain proper array structure
        const updatedNotes = [...notes.value];
        updatedNotes.splice(originalIndex, 1);
        notes.value = updatedNotes;

        await saveNotesToFirebase();
    } catch (error) {
        console.error('Error deleting note:', error);
        // Revert the optimistic update
        loadNotes();
    } finally {
        isLoading.value = false;
    }
};

const saveNotesToFirebase = async () => {
    if (!props.clientId) {
        console.error('No clientId provided for saving notes');
        return;
    }

    try {
        const db = tenantDatabase();

        console.log('Saving notes to Firebase clientId:', props.clientId);
        console.log('Notes data:', notes.value);

        // Save the notes array directly to the notes field
        const clientRef = dbRef(db, `clients/${props.clientId}`);

        // Ensure we're saving a proper array, not an object with numeric keys
        // Force array creation by spreading into a new array
        const notesArray = Array.isArray(notes.value) ? [...notes.value] : Object.values(notes.value);

        console.log('Original notes.value:', notes.value);
        console.log('Original is array?', Array.isArray(notes.value));
        console.log('Original constructor:', notes.value.constructor.name);
        console.log('Saving as array:', notesArray);
        console.log('Final is array?', Array.isArray(notesArray));
        console.log('Final constructor:', notesArray.constructor.name);

        // Use set instead of update to avoid Firebase converting array to object
        const notesRef = dbRef(db, `clients/${props.clientId}/notes`);
        await set(notesRef, notesArray);

        console.log('Notes saved successfully to Firebase');

        // Emit to parent so it knows the data has changed
        emit('notesUpdated', notesArray);

        console.log('Emitted notesUpdated event');

        // Don't reload from Firebase - just trust the save worked
        // The parent component will handle the data refresh
    } catch (error) {
        console.error('Error saving notes:', error);
        console.error('ClientId was:', props.clientId);
        console.error('Notes data was:', notes.value);
        throw error;
    }
};

// Add method to reload notes from Firebase
const reloadNotesFromFirebase = async () => {
    if (!props.clientId) return;

    try {
        const db = tenantDatabase();
        const clientRef = dbRef(db, `clients/${props.clientId}`);
        const snapshot = await get(clientRef);

        if (snapshot.exists()) {
            const clientData = snapshot.val();
            console.log('Reloaded client data:', clientData.notes);

            if (clientData.notes) {
                if (Array.isArray(clientData.notes)) {
                    notes.value = [...clientData.notes];
                } else if (typeof clientData.notes === 'string') {
                    // Handle legacy string format if still exists
                    notes.value = [{
                        text: clientData.notes,
                        author: 'Unknown User',
                        authorId: 'unknown',
                        createdAt: Date.now(),
                        updatedAt: Date.now()
                    }];
                }

                // Update parent component too
                emit('notesUpdated', notes.value);
            }
        }
    } catch (error) {
        console.error('Error reloading notes from Firebase:', error);
    }
};

const canEditNote = (note) => {
    const user = currentUser.value;
    if (!user) return false;

    // User can edit their own notes
    if (user.uid === note.authorId) return true;

    // Admin can edit all notes (check both userSettings and direct permission)
    if (user.userSettings?.permission === 'admin') return true;

    // Also check if user has admin role in Firebase custom claims (if available)
    if (user.customClaims?.admin) return true;

    return false;
};
</script>

<template>
    <div class="multi-notes">
        <!-- Content -->
        <div>
            <!-- Loading overlay -->
            <div v-if="isLoading" class="absolute inset-0 bg-white bg-opacity-75 flex items-center justify-center z-10 rounded-lg">
                <div class="flex items-center space-x-2">
                    <div class="animate-spin rounded-full h-4 w-4 border-b-2 border-blue-600"></div>
                    <span class="text-sm text-gray-600">Saving...</span>
                </div>
            </div>

            <div class="mb-4 relative">
                <div class="flex justify-end mb-3">
                    <button
                        @click="showAddForm = true"
                        :disabled="isLoading"
                        class="font-medium inline-flex items-center justify-center border border-grey-900 rounded py-2 px-4 text-sm disabled:opacity-50 transition-colors"
                        type="button"
                    >
                        Add Note
                    </button>
                </div>

            <!-- Add Note Form -->
            <div v-if="showAddForm" class="mb-4 p-4 border border-gray-200 rounded-lg bg-gray-50">
                <Editor v-model="newNoteText"
                    api-key="2m9gvnt4gi3wlqn57wwmcn24llv29udji8c93chueqi45q6k"
                    :init="{
                        menubar: false,
                        plugins: 'lists link',
                        toolbar: 'link bullist',
                        min_height: 150,
                        height: 150
                    }" />
                <div class="flex justify-end gap-2 mt-3">
                    <button
                        @click="showAddForm = false; newNoteText = ''"
                        class="px-3 py-1.5 text-xs border border-gray-300 rounded-md hover:bg-gray-100 transition-colors"
                    >
                        Cancel
                    </button>
                    <button
                        @click="addNote"
                        :disabled="!newNoteText.trim() || isLoading"
                        class="px-3 py-1.5 text-xs bg-orange-500 text-white rounded-md hover:bg-orange-600 disabled:opacity-50 disabled:cursor-not-allowed transition-colors"
                    >
                        Add Note
                    </button>
                </div>
            </div>

            <!-- Notes List -->
            <div class="space-y-3">
                <div
                    v-for="(note, index) in sortedNotes"
                    :key="index"
                    class="p-4 border border-gray-200 rounded-lg bg-white"
                >
                    <div class="flex justify-between items-start mb-2">
                        <div>
                            <span class="text-sm font-medium text-gray-900">{{ note.author || 'Unknown User' }}</span>
                            <span class="text-xs text-gray-500 ml-2">{{ formatDate(note.createdAt) }}</span>
                            <span v-if="note.lastEditedBy && note.updatedAt !== note.createdAt" class="text-xs text-gray-400 ml-2">
                                (edited {{ formatDate(note.updatedAt) }})
                            </span>
                        </div>
                        <div v-if="canEditNote(note)" class="flex gap-1">
                            <button
                                @click="startEdit(note.originalIndex, note.text)"
                                class="px-2 py-1 text-xs text-gray-600 hover:text-blue-600 transition-colors"
                                title="Edit note"
                            >
                                Edit
                            </button>
                            <button
                                @click="deleteNote(note.originalIndex)"
                                class="px-2 py-1 text-xs text-gray-600 hover:text-red-600 transition-colors"
                                title="Delete note"
                            >
                                Delete
                            </button>
                        </div>
                    </div>

                    <!-- Note Text (View Mode) -->
                    <div v-if="editingIndex !== note.originalIndex" class="text-sm text-gray-700" v-html="note.text">
                    </div>

                    <!-- Note Text (Edit Mode) -->
                    <div v-else class="space-y-3">
                        <Editor v-model="editingText"
                            api-key="2m9gvnt4gi3wlqn57wwmcn24llv29udji8c93chueqi45q6k"
                            :init="{
                                menubar: false,
                                plugins: 'lists link',
                                toolbar: 'link bullist',
                                min_height: 150,
                                height: 150
                            }" />
                        <div class="flex justify-end gap-2">
                            <button
                                @click="cancelEdit"
                                class="px-3 py-1.5 text-xs border border-gray-300 rounded-md hover:bg-gray-100 transition-colors flex items-center"
                            >
                                <PlusClose class="w-3 h-3 mr-1" />
                                Cancel
                            </button>
                            <button
                                @click="saveEdit"
                                :disabled="!editingText.trim() || isLoading"
                                class="px-3 py-1.5 text-xs bg-orange-500 text-white rounded-md hover:bg-orange-600 disabled:opacity-50 disabled:cursor-not-allowed transition-colors flex items-center"
                            >
                                <IconTick class="w-3 h-3 mr-1" />
                                Save
                            </button>
                        </div>
                    </div>
                </div>

                <!-- Empty State -->
                <div v-if="!sortedNotes.length" class="text-center py-8 text-gray-500">
                    <p v-if="props.isLoading || (!props.notes && props.notes !== '')" class="text-sm">Loading notes...</p>
                    <template v-else>
                        <p class="text-sm">No notes yet.</p>
                        <button
                            @click="showAddForm = true"
                            class="mt-2 text-blue-600 hover:text-blue-800 text-sm"
                        >
                            Add the first note
                        </button>
                    </template>
                </div>
            </div>
        </div>
        </div>
    </div>
</template>

<style scoped>
.multi-notes {
    max-width: 100%;
}
</style>